import { Extension } from "@tiptap/core";
import { Node as PMNode } from "@tiptap/pm/model";
import { EditorState, Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export interface MentionDecorationOptions {
  // Latest workspace member display names, via a getter (they load async from the people directory,
  // so a value captured at editor-creation would miss them). Only "@<name>" runs that match one of
  // these are chipped.
  getMemberNames: () => string[];
}

// Build the "@(Name1|Name2|…)\b" matcher — longest name first so a short name can't match inside a
// longer one, regex metachars escaped. Mirrors the comment renderer's highlightMentions, so the
// editor chips exactly what the read-only render links.
function mentionRegex(memberNames: string[]): RegExp | null {
  if (memberNames.length === 0) return null;
  const sorted: string[] = [...memberNames].sort((a: string, b: string) => b.length - a.length);
  const alternation: string = sorted.map((name: string) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  return new RegExp(`@(${alternation})\\b`, "g");
}

// Decoration-only overlay (never mutates the doc/markdown — the stored Markdown stays plain "@Name")
// that chips "@Name" runs for known members live in the editor. The editor-side counterpart to the
// comment renderer's mention links.
function buildDecorations(doc: PMNode, memberNames: string[]): DecorationSet {
  const re: RegExp | null = mentionRegex(memberNames);
  if (!re) return DecorationSet.empty;
  const decorations: Decoration[] = [];
  doc.descendants((node: PMNode, pos: number) => {
    if (!node.isText || !node.text) return;
    const text: string = node.text;
    const scan: RegExp = new RegExp(re.source, "g");
    let match: RegExpExecArray | null;
    while ((match = scan.exec(text)) !== null) {
      const from: number = pos + match.index;
      decorations.push(
        Decoration.inline(from, from + match[0].length, {
          class: "kuika-board__mention-chip",
          nodeName: "span"
        })
      );
    }
  });
  return DecorationSet.create(doc, decorations);
}

export const MentionDecoration: Extension<MentionDecorationOptions> = Extension.create<MentionDecorationOptions>({
  name: "mentionDecoration",
  addOptions() {
    return { getMemberNames: () => [] };
  },
  addProseMirrorPlugins() {
    const getMemberNames: () => string[] = this.options.getMemberNames;
    // A fresh key PER editor instance — a shared module-level key collides when both the description
    // and comment editors mount this extension (the second one's decorations would vanish).
    const key: PluginKey<DecorationSet> = new PluginKey<DecorationSet>("mentionDecoration");
    // Rebuild when the doc changes OR the member list does (it loads after the editor mounts; the
    // host dispatches an empty transaction to drive that second rebuild).
    let lastNamesKey = "";
    return [
      new Plugin<DecorationSet>({
        key,
        state: {
          init: (_config, instance: EditorState): DecorationSet => {
            lastNamesKey = getMemberNames().join(" ");
            return buildDecorations(instance.doc, getMemberNames());
          },
          apply: (tr, old: DecorationSet): DecorationSet => {
            const namesKey: string = getMemberNames().join(" ");
            if (tr.docChanged || namesKey !== lastNamesKey) {
              lastNamesKey = namesKey;
              return buildDecorations(tr.doc, getMemberNames());
            }
            return old;
          }
        },
        props: {
          decorations(state: EditorState): DecorationSet | undefined {
            return key.getState(state);
          }
        }
      })
    ];
  }
});
